import React from "react";
import { Icon } from "../registry";
import type { SiteContent } from "./types";
import { fontCss } from "./util";

/* ============================================================
   Website builder — the preview column.
   Live: an iframe on the tenant's preview URL (?__preview=token), rendered at
   a real 1280 px desktop / 390 px phone width and scaled to fit the column.
   It remounts after every successful autosave (nonce).
   Sample (sandbox / no service): a small in-page mock of the home page drawn
   from the draft, so edits still show somewhere.
   ============================================================ */

export function PreviewPane({ url, path, nonce, device, setDevice, sample, draft, onReload, openUrl }: any) {
  const boxRef = React.useRef<HTMLDivElement>(null);
  const [box, setBox] = React.useState({ w: 0, h: 0 });
  const [loading, setLoading] = React.useState(true);
  React.useEffect(() => {
    const el = boxRef.current; if (!el) return;
    const measure = () => setBox({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const RO = (window as any).ResizeObserver;
    if (!RO) { window.addEventListener("resize", measure); return () => window.removeEventListener("resize", measure); }
    const ro = new RO(measure); ro.observe(el);
    return () => ro.disconnect();
  }, []);
  React.useEffect(() => { setLoading(true); }, [url, nonce]);

  const frameW = device === "phone" ? 390 : 1280;
  const pad = device === "phone" ? 24 : 0;
  const scale = box.w ? Math.min(1, (box.w - pad) / frameW) : 0.4;
  const frameH = box.h ? Math.max(400, (box.h - (device === "phone" ? 24 : 0)) / scale) : 900;
  // desktop: pinned top-left and scaled to the column; phone: centered, framed, 12 px down
  const scalerStyle: React.CSSProperties = device === "phone"
    ? { width: frameW, height: frameH, left: "50%", top: 12, transform: `translateX(-50%) scale(${scale})`, transformOrigin: "top center" }
    : { width: frameW, height: frameH, left: 0, top: 0, transform: `scale(${scale})`, transformOrigin: "top left" };

  return (
    <div className="ws-preview card">
      <div className="ws-pv-bar">
        <div className="ws-seg" role="tablist" aria-label="Preview size">
          <button type="button" className={device !== "phone" ? "on" : ""} onClick={() => setDevice("desktop")}>Desktop</button>
          <button type="button" className={device === "phone" ? "on" : ""} onClick={() => setDevice("phone")}>Phone</button>
        </div>
        <code className="ws-pv-path" title={url || ""}>{sample ? "sample preview" : path}</code>
        {!sample && <button type="button" className="ws-ib" title="Reload preview" aria-label="Reload preview" onClick={onReload}><Icon name="undo" size={14} /></button>}
        {!sample && openUrl && <a className="ws-ib" href={openUrl} target="_blank" rel="noopener" title="Open this page in a new tab" aria-label="Open preview in a new tab"><Icon name="arrow" size={14} /></a>}
      </div>
      <div ref={boxRef} className={"ws-pv-box" + (device === "phone" ? " phone" : "")}>
        {sample ? (
          <div className="ws-pv-scaler" style={scalerStyle}>
            <SamplePreview d={draft} phone={device === "phone"} />
          </div>
        ) : url ? (
          <div className="ws-pv-scaler" style={scalerStyle}>
            <iframe key={nonce + ":" + url} src={url} title="Website preview" onLoad={() => setLoading(false)}
              style={{ width: frameW, height: frameH, border: 0, background: "#fff", display: "block" }} />
          </div>
        ) : <div className="muted" style={{ padding: 24 }}>No preview yet.</div>}
        {!sample && url && loading && <div className="ws-pv-loading"><span className="ws-spin" />Loading preview…</div>}
      </div>
    </div>
  );
}

/* a faithful-enough sketch of the Kodiak home page, from the draft */
function SamplePreview({ d, phone }: { d: SiteContent; phone: boolean }) {
  if (!d) return null;
  const c = d.brand.colors;
  const b = d.business;
  const styles = d.styles.items.filter((s) => s.on);
  const cols = phone ? 1 : 3;
  const wrap = { maxWidth: 1120, margin: "0 auto", padding: phone ? "0 18px" : "0 40px" };
  const logo = d.brand.logo.onDark;
  return (
    <div style={{ background: c.cream, color: c.body, fontFamily: fontCss(d.brand.font), minHeight: "100%", fontSize: 16, lineHeight: 1.55 }}>
      <div style={{ background: c.ink, color: "#fff" }}>
        <div style={{ ...wrap, display: "flex", alignItems: "center", gap: 18, height: 72 }}>
          {logo ? <img src={logo} alt="" style={{ height: 38 }} /> : <b style={{ fontSize: 20, letterSpacing: -0.3 }}>{b.shortName || b.name}</b>}
          <span style={{ flex: 1 }} />
          {!phone && ["Styles", "Locations", "About", "Reviews"].map((x) => <span key={x} style={{ opacity: 0.8, fontSize: 14 }}>{x}</span>)}
          <span style={{ background: c.accent, color: "#fff", padding: "9px 16px", borderRadius: 8, fontWeight: 700, fontSize: 14 }}>{phone ? "Call" : b.phone.display}</span>
        </div>
      </div>
      <div style={{ position: "relative", background: c.ink, color: "#fff" }}>
        {d.home.heroPhoto && d.home.heroPhoto.src && <img src={d.home.heroPhoto.src} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", opacity: 0.38 }} />}
        {d.home.heroPhoto && d.home.heroPhoto.example && <span style={{ position: "absolute", right: 12, bottom: 12, fontSize: 11, background: "rgba(0,0,0,.55)", padding: "3px 8px", borderRadius: 6 }}>Example photo</span>}
        <div style={{ ...wrap, position: "relative", padding: phone ? "56px 18px" : "96px 40px" }}>
          <div style={{ color: c.accent, fontWeight: 700, fontSize: 13, letterSpacing: 1, textTransform: "uppercase" }}>{d.home.eyebrow}</div>
          <h1 style={{ fontSize: phone ? 34 : 54, lineHeight: 1.05, margin: "14px 0", maxWidth: 760, color: "#fff", fontWeight: 800 }}>{d.home.h1}</h1>
          <p style={{ fontSize: phone ? 16 : 19, maxWidth: 620, opacity: 0.9, margin: 0 }}>{d.home.lead}</p>
          <div style={{ display: "flex", gap: 12, marginTop: 26, flexWrap: "wrap" }}>
            <span style={{ background: c.accent, color: "#fff", padding: "14px 22px", borderRadius: 10, fontWeight: 800 }}>Get my free quote</span>
            <span style={{ border: "1px solid rgba(255,255,255,.35)", padding: "14px 22px", borderRadius: 10, fontWeight: 700 }}>{b.phone.display}</span>
          </div>
        </div>
      </div>
      <div style={{ ...wrap, padding: phone ? "44px 18px" : "72px 40px" }}>
        <h2 style={{ color: c.heading, fontSize: phone ? 26 : 36, margin: "0 0 22px", fontWeight: 800 }}>{d.home.whyTitle}</h2>
        <div style={{ display: "grid", gridTemplateColumns: `repeat(${phone ? 1 : 2}, 1fr)`, gap: 16 }}>
          {d.home.why.map((w, i) => (
            <div key={i} style={{ background: "#fff", borderRadius: 14, padding: 20, borderTop: `3px solid ${c.accent}` }}>
              <b style={{ color: c.heading, fontSize: 18 }}>{w.t}</b>
              <p style={{ margin: "6px 0 0" }}>{w.d}</p>
            </div>
          ))}
        </div>
        <h2 style={{ color: c.heading, fontSize: phone ? 26 : 36, margin: "56px 0 22px", fontWeight: 800 }}>{d.home.servicesTitle}</h2>
        <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 16 }}>
          {styles.map((s) => (
            <div key={s.slug} style={{ background: "#fff", borderRadius: 14, overflow: "hidden" }}>
              <div style={{ height: 150, background: c.ink, position: "relative" }}>{s.thumb && s.thumb.src && <img src={s.thumb.src} alt="" loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover" }} />}
                {s.tag && <span style={{ position: "absolute", left: 10, top: 10, background: c.accent, color: "#fff", fontSize: 11, fontWeight: 800, padding: "3px 8px", borderRadius: 6 }}>{s.tag}</span>}</div>
              <div style={{ padding: 16 }}><b style={{ color: c.heading, fontSize: 18 }}>{s.name}</b><p style={{ margin: "4px 0 0", fontSize: 14 }}>{s.tagline}</p></div>
            </div>
          ))}
        </div>
      </div>
      <div style={{ background: c.ink, color: "#fff" }}>
        <div style={{ ...wrap, padding: phone ? "40px 18px" : "56px 40px" }}>
          <h2 style={{ fontSize: phone ? 24 : 32, margin: 0, fontWeight: 800 }}>{d.home.areaTitle}</h2>
          <p style={{ opacity: 0.85, maxWidth: 640 }}>{d.home.areaBlurb}</p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>{d.areas.cities.filter((x) => x.on).map((x) => <span key={x.slug} style={{ border: `1px solid ${c.accent}`, color: "#fff", borderRadius: 99, padding: "5px 12px", fontSize: 14 }}>{x.name}</span>)}</div>
          <div style={{ marginTop: 30, opacity: 0.7, fontSize: 13 }}>{b.name} · {b.address.city}, {b.address.region} · {b.phone.display}{b.insured ? " · Insured" : ""}{d.claims.warrantyText ? " · " + d.claims.warrantyText : ""}</div>
        </div>
      </div>
    </div>
  );
}
